import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["body", "footer", "button"];
  static values = {
    showUrl: String,
    createUrl: String,
    pending: Boolean,
    admin: Boolean,
  };

  connect() {
    if (this.pendingValue) {
      this.disableButton();
      this.startPolling();
    }
  }

  create(event) {
    event.preventDefault();

    const csrfToken = document.querySelector("meta[name='csrf-token']")?.content;

    fetch(this.createUrlValue, {
      method: "POST",
      headers: {
        "Accept": "application/json",
        "X-CSRF-Token": csrfToken,
      },
    })
      .then((response) => response.json())
      .then(() => {
        this.showLoading();
        this.startPolling();
      });
  }

  showLoading() {
    this.bodyTarget.innerHTML = `
      <div class="text-center py-3">
        <div class="spinner-border spinner-border-sm text-info me-2" role="status"></div>
        <span>コメントを生成中...</span>
      </div>
    `;
    // Admins can regenerate repeatedly, so keep the button (disabled while
    // generating); for everyone else the action is one-shot — remove it.
    if (this.adminValue) {
      this.disableButton();
    } else {
      this.removeFooter();
    }
  }

  removeFooter() {
    if (this.hasFooterTarget) {
      this.footerTarget.remove();
    }
  }

  disableButton() {
    if (this.hasButtonTarget) {
      this.buttonTarget.disabled = true;
    }
  }

  enableButton() {
    if (this.hasButtonTarget) {
      this.buttonTarget.disabled = false;
    }
  }

  finishRender() {
    if (this.adminValue) {
      this.enableButton();
    } else {
      this.removeFooter();
    }
  }

  startPolling() {
    this.pollTimer = setInterval(() => {
      fetch(this.showUrlValue, {
        headers: { "Accept": "application/json" },
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.status === "completed") {
            this.stopPolling();
            this.renderCompleted(data);
          } else if (data.status === "failed") {
            this.stopPolling();
            this.renderFailed(data);
          }
        });
    }, 3000);
  }

  stopPolling() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  disconnect() {
    this.stopPolling();
  }

  renderCompleted(data) {
    const sections = [
      { key: "input_check", title: "入力ミスチェック", icon: "search", style: "warning" },
      { key: "general_advice", title: "一般的なアドバイス", icon: "lightbulb", style: "info" },
      { key: "advanced_advice", title: "高度なアドバイス", icon: "chart-line", style: "secondary" },
    ];

    const html = sections.map((section) => {
      const content = this.escapeHtml(data[section.key] || "");
      return `
        <div class="mb-3">
          <h6 class="text-${section.style}">
            <i class="fas fa-${section.icon} me-1"></i>${section.title}
          </h6>
          <p style="white-space: pre-wrap;">${content}</p>
        </div>
      `;
    }).join("");

    const meta = data.meta
      ? `<small class="text-muted d-block mt-2">
          <a data-bs-toggle="collapse" href="#ai-comment-meta" class="text-muted">
            <i class="fas fa-info-circle"></i> 詳細情報
          </a>
          <div class="collapse" id="ai-comment-meta">
            モデル: ${this.escapeHtml(data.meta.ai_model)}
             / トークン: ${data.meta.total_tokens}
             / コスト: $${data.meta.cost_usd}
             / 生成時間: ${data.meta.generation_time_ms}ms
          </div>
        </small>`
      : "";

    this.bodyTarget.innerHTML = html + meta;
    this.finishRender();
  }

  renderFailed(data) {
    const errorMessage = this.escapeHtml(data.error || "不明なエラー");
    this.bodyTarget.innerHTML = `
      <p class="text-danger">コメントの生成に失敗しました: ${errorMessage}</p>
    `;
    this.finishRender();
  }

  escapeHtml(text) {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }
}
